import { createColumnHelper } from '@tanstack/react-table';
import React from 'react';

import { Button, ButtonTypes } from '@common/components';
import { useCustomSorting } from '@common/components/DatapharmTable/useCustomSorting';
import { SubmissionActions } from '@common/components/SubmissionActions';
import { SubmissionStatus } from '@common/components/SubmissionStatus';
import { SubmissionTaskActions } from '@common/components/SubmissionTaskActions';
import { SubmissionTaskStatus } from '@common/components/SubmissionTaskStatus';
import { ICompanySubmission } from '@common/features/submission/types';
import { formatPeriodInDays, getSubmissionTaskTypeDisplayValue, getTaskIcon, isStringNullOrEmpty } from '@common/helpers';
import { formatDateTime } from '@common/helpers';
import { SubmissionStatus as SubmissionStatusType, SubmissionTaskStatuses } from '@common/services/types';
import { AllowedSubmissionActions, CustomerAllowedTaskActions, DocumentType, Id, SubmissionTaskType } from '@common/types';

import { getDocumentAttributes } from '@common/features/document/helpers';
import { StyledSVG } from '@common/styles';
import * as Styled from './styles';
import { ViewContentLink } from '@common/components/ViewContentLink/ViewContentLink';
import { IDocumentContentInfo } from '@common/hooks/useFetchDocumentHtmlContent';

interface ISubmissionProps {
  onCancel: (submissionId: number) => void;
  onRemove: (submissionId: number) => void;
  onSubmissionDetailsClick: (submissionId: number, status: SubmissionStatusType, existingProductGroupId?: number) => void;
}

export const useSubmissionsTableSorting = () => {
  var columnTranslations = {
    submissionTitle: 'Title',
    status: 'Status',
    daysInProgress: 'DaysInProgress',
    submittedAt: 'DateSubmitted',
    submittedBy: 'SubmittedBy',
  };

  return useCustomSorting<ICompanySubmission>(columnTranslations);
};

export const prepareSubmissionsColumns = ({ onCancel, onSubmissionDetailsClick, onRemove }: ISubmissionProps) => {
  const columnHelper = createColumnHelper<Omit<ICompanySubmission, 'expandableContent'>>();

  return [
    columnHelper.accessor('submissionTitle', {
      header: 'Submission ID_title',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => <SubmissionStatus value={getValue() as SubmissionStatusType} />,
    }),
    columnHelper.accessor('daysInProgress', {
      header: 'Days in progress',
      cell: ({ getValue }) => formatPeriodInDays(getValue()),
    }),
    columnHelper.accessor('submittedAt', {
      header: 'Date and Time Submitted',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? formatDateTime(new Date(value)) : '-';
      },
    }),
    columnHelper.accessor('submittedBy', {
      header: 'Submitted By',
    }),
    columnHelper.display({
      header: 'Details',
      cell: ({ row }) => (
        <Button
          type={ButtonTypes.TEXT}
          text=""
          icon="/icons/eye.svg"
          height={25}
          onClick={() =>
            onSubmissionDetailsClick(
              row.original.submissionId,
              row.original.status as SubmissionStatusType,
              row.original.existingProductGroupId,
            )
          }
        />
      ),
    }),
    columnHelper.display({
      header: 'Actions',
      cell: ({ row }) => {
        const allowedActions = [];

        if (
          row.original.status === SubmissionStatusType.InProgress ||
          (row.original.status === SubmissionStatusType.Draft && row.original.containsTasks)
        ) {
          allowedActions.push(AllowedSubmissionActions.Cancel);
        }

        if (row.original.status === SubmissionStatusType.Draft && !row.original.containsTasks) {
          allowedActions.push(AllowedSubmissionActions.Remove);
        }

        return (
          <SubmissionActions
            allowedActions={allowedActions}
            onCancel={() => onCancel(row.original.submissionId)}
            onRemove={() => onRemove(row.original.submissionId)}
          />
        );
      },
    }),
  ];
};

export interface ISubmissionTaskTableRow {
  type: SubmissionTaskType;
  documentName: string;
  documentType: DocumentType;
  documentId: number;
  name: string;
  version: number;
  title: string;
  fastTrack: boolean;
  status: string;
  sla: string;
  submissionId: number;
  submissionTaskId: number;
  allowedCustomerActions: CustomerAllowedTaskActions[];
  link: string;
  customerLink: string | null;
  hasDocument: boolean;
}

interface ISubmissionTaskProps {
  onApprove: (taskId: number, submissionId: number, index: number) => void;
  onCancel: (taskId: number, submissionId: number, index: number) => void;
  onRequestChanges: (taskId: number, submissionId: number, index: number) => void;
  onChangeBroadcast: (taskId: number, submissionId: number) => void;
  onDocumentTaskDetailsClick: (taskId: number, submissionId: number) => void;
  onProductTaskDetailsClick: (submissionId: number) => void;
  onFileOpen: (taskId: Id) => void;
  onViewConvertedContent: (submissionTaskId: string, submissionId: string) => void;
  onViewHtmlContent: (documentData: IDocumentContentInfo) => void;
  onViewPublishedContent: (submissionTaskId: string, submissionId: string) => void;
}

export const prepareDetailsColumns = ({
  onApprove,
  onCancel,
  onRequestChanges,
  onChangeBroadcast,
  onDocumentTaskDetailsClick,
  onProductTaskDetailsClick,
  onFileOpen,
  onViewConvertedContent,
  onViewHtmlContent,
  onViewPublishedContent
}: ISubmissionTaskProps) => {
  const columnHelper = createColumnHelper<ISubmissionTaskTableRow>();

  return [
    columnHelper.accessor('type', {
      header: 'Type',
      cell: ({ getValue, row }) => {
        const taskType = getValue() as SubmissionTaskType;
        const contentType = row.original.documentName;
        return (
          <Styled.TaskType>
            {getSubmissionTaskTypeDisplayValue(taskType, contentType)}
            <StyledSVG height={25} src={getTaskIcon(taskType)} />
          </Styled.TaskType>
        );
      },
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row, getValue }) => {
        const documentAttributes = getDocumentAttributes(row.original.documentType, false);
        var isAudioVideoWithoutUrl =
          documentAttributes.showAudioVideoWebLink &&
          isStringNullOrEmpty(row.original.link) &&
          isStringNullOrEmpty(row.original.customerLink);

        return (
          <ViewContentLink
            id={row.original.submissionTaskId!}
            text={getValue()}
            isHtml={documentAttributes.hasHtmlContent}
            documentData={{
              documentId: row.original.documentId,
              version: row.original.version,
              type: row.original.documentType,
              submissionId: row.original.submissionId,
            }}
            disabled={!row.original.hasDocument || isAudioVideoWithoutUrl}
            link={isStringNullOrEmpty(row.original.link) ? row.original.customerLink : row.original.link}
            onViewContent={onViewHtmlContent}
            onFileOpen={onFileOpen}
          />
        );
      },
    }),
    columnHelper.accessor('submissionTaskId', {
      header: 'Task ID',
    }),
    columnHelper.accessor('fastTrack', {
      header: 'Fast track',
      cell: ({ getValue }) =>
        getValue() && (
          <Styled.CenterFlex>
            <StyledSVG src="/icons/red_flag.svg" />
          </Styled.CenterFlex>
        ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => <SubmissionTaskStatus value={getValue() as SubmissionTaskStatuses} />,
    }),
    columnHelper.accessor('sla', {
      header: 'SLA',
      cell: ({ getValue }) => `${getValue()} days`,
    }),
    columnHelper.display({
      header: 'Details',
      cell: ({
        row: {
          original: { submissionId, submissionTaskId, type },
        },
      }) => (
        <Button
          type={ButtonTypes.TEXT}
          text=""
          icon="/icons/eye.svg"
          height={25}
          onClick={() =>
            type === SubmissionTaskType.ProductDiscontinue
              ? onProductTaskDetailsClick(submissionId)
              : onDocumentTaskDetailsClick(submissionTaskId, submissionId)
          }
        />
      ),
    }),
    columnHelper.accessor('allowedCustomerActions', {
      id: 'actions',
      header: 'Actions',
      cell: ({
        getValue,
        row: {
          index,
          original: { submissionId, submissionTaskId },
        },
      }) => (
        <SubmissionTaskActions
          data-testid={`action-${submissionTaskId}`}
          allowedActions={getValue()}
          onApprove={() => onApprove(submissionTaskId, submissionId, index)}
          onCancel={() => onCancel(submissionTaskId, submissionId, index)}
          onRequestChange={() => onRequestChanges(submissionTaskId, submissionId, index)}
          onChangeBroadcast={() => onChangeBroadcast(submissionTaskId, submissionId)}
          onViewConvertedContent={() => onViewConvertedContent(submissionTaskId.toString(), submissionId.toString())}
          onViewPublishedContent={() => { onViewPublishedContent(submissionTaskId.toString(), submissionId.toString()) }
          }
          onReview={() => onDocumentTaskDetailsClick(submissionTaskId, submissionId)}
        />
      ),
    }),
  ];
};
